import { TransactionResponse } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { MAX_APPROVE } from '@/common/constants';
import { isWalletConnect } from '@/common/util';
import { erc20Abi } from '@/contracts/abis/ERC20ABI';
import BaseContract from '@/contracts/baseContract';

export interface ContractError {
  code: string;
  cancelled: boolean;
  replacement: { hash: string };
}

class ERC20Contract extends BaseContract {
  private contractAddress: string;

  constructor(networkId: number, contractAddress: string) {
    super(networkId);
    this.contractAddress = contractAddress;
  }

  async balanceOf(address: string): Promise<BigNumber> {
    return await this._callWeb3WithServerFallback(
      this.contractAddress,
      erc20Abi,
      'balanceOf',
      [address],
      false,
    );
  }

  async getAllowance(spender: string, owner: string): Promise<BigNumber> {
    return await this._callWeb3WithServerFallback(
      this.contractAddress,
      erc20Abi,
      'allowance',
      [owner, spender],
      false,
    );
  }

  async getERC20Name(): Promise<string> {
    return await this._callWeb3WithServerFallback(
      this.contractAddress,
      erc20Abi,
      'name',
      [],
      false,
    );
  }

  async getERC20Symbol(): Promise<string> {
    return await this._callWeb3WithServerFallback(
      this.contractAddress,
      erc20Abi,
      'symbol',
      [],
      false,
    );
  }

  async getERC20Decimals(): Promise<number> {
    return this._callWeb3WithServerFallback(this.contractAddress, erc20Abi, 'decimals', [], false);
  }

  async approve(walletAddress: string, spender: string): Promise<TransactionResponse> {
    try {
      // if using wallet connect with no provider, we will use wallet connect built in provider to make write calls
      const unchecked = isWalletConnect();

      const gasLimit = await this.estimateGasApprove(walletAddress, spender);

      return await this._getContractInstance(
        this.contractAddress,
        erc20Abi,
        true,
        false,
        unchecked,
      ).approve(spender, MAX_APPROVE, {
        gasLimit,
      });
    } catch (e: unknown) {
      return await this.errorHandling(e as ContractError);
    }
  }

  async estimateGasApprove(walletAddress: string, spender: string): Promise<BigNumber> {
    const args = [spender, MAX_APPROVE, { from: walletAddress }];
    const functionSig = 'approve(address,uint256)';

    return this._estimateGasWithServerFallback(
      this.network,
      this.contractAddress,
      erc20Abi,
      functionSig,
      args,
    );
  }

  async errorHandling(error: ContractError): Promise<TransactionResponse> {
    if (error.code === 'TRANSACTION_REPLACED' && !error.cancelled && error.replacement) {
      const provider = window.ManifoldEthereumProvider.provider();
      if (!provider) {
        throw new Error('No web3 provider detected, please refresh the page and try again');
      }
      return await provider.getTransaction(error.replacement.hash);
    } else {
      throw error;
    }
  }
}

export default ERC20Contract;
